const MODULE_CODE = 'member_register'
const MOBILE_VERIFY_CONFIG_MODULE_CODE = 'mobile_verify_config'
const EMAIL_VERIFY_CONTEXT_KEY = 'member_register_email_verify_context'

export default {
  methods: {
    getRegisterPhoneSyncKey() {
      return `liff_register_mobile_phone:${this.$route.params.orgCode}`;
    },
    getRegisterValidatePhoneSnapshotKey() {
      return `liff_register_validate_mobile_phone:${this.$route.params.orgCode}`;
    },
    getSyncedMobilePhone() {
      if (typeof window === 'undefined') {
        return null;
      }
      return sessionStorage.getItem(this.getRegisterPhoneSyncKey()) || null;
    },
    setSyncedMobilePhone(mobilePhone) {
      if (typeof window === 'undefined') {
        return null;
      }

      const normalizedPhone = `${mobilePhone || ''}`.trim();
      const syncKey = this.getRegisterPhoneSyncKey();

      if (!normalizedPhone) {
        sessionStorage.removeItem(syncKey);
        return null;
      }

      sessionStorage.setItem(syncKey, normalizedPhone);
      this.$store.commit("liffAuth/SET_MOBILE", normalizedPhone);
      return normalizedPhone;
    },
    getRegisterValidateMobilePhoneSnapshot() {
      if (typeof window === 'undefined') {
        return null;
      }

      return sessionStorage.getItem(this.getRegisterValidatePhoneSnapshotKey()) || null;
    },
    setRegisterValidateMobilePhoneSnapshot(mobilePhone) {
      if (typeof window === 'undefined') {
        return null;
      }

      const normalizedPhone = `${mobilePhone || ''}`.trim();
      const snapshotKey = this.getRegisterValidatePhoneSnapshotKey();

      if (!normalizedPhone) {
        sessionStorage.removeItem(snapshotKey);
        return null;
      }

      sessionStorage.setItem(snapshotKey, normalizedPhone);
      return normalizedPhone;
    },
    isReturnedFromRegisterValidate() {
      return this.$route.query.from === 'register_validate';
    },
    isSameAsRegisterValidateMobilePhoneSnapshot(mobilePhone) {
      return `${mobilePhone || ''}`.trim() === this.getRegisterValidateMobilePhoneSnapshot();
    },
    sanitizeRegisterQuery(extraQuery = {}) {
      const nextQuery = {
        ...this.$route.query,
        ...extraQuery,
      };
      delete nextQuery.mobile_phone;
      return nextQuery;
    },
    async migrateLegacyMobilePhoneQuery() {
      const legacyMobilePhone = this.$route?.query?.mobile_phone;
      if (!legacyMobilePhone) {
        return;
      }

      this.setSyncedMobilePhone(legacyMobilePhone);
      const nextQuery = this.sanitizeRegisterQuery();
      await this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
      });
    },
    async goNext(next, additionalQuery = {}) {
      await this.$store.dispatch('liffAuth/fetchMe');

      switch (next) {
        case "registered_page":
          await this.goSuccess();
          return false;
        case "success_page":
          await this.$router.push({
            name: "LiffRegisterWelcome",
            params: {
              orgCode: this.$route.params.orgCode,
            },
          });
          return false;
        case "form_page":
          if (!['LiffRegisterMember', 'LiffRegisterTerms'].includes(this.$route.name)) {
            await this.$router.push({
              name: "LiffRegisterMember",
              params: {
                orgCode: this.$route.params.orgCode,
              },
              query: this.buildPublicRouteQuery(additionalQuery),
            });
            return false;
          }
          break;
        case "verify_page":
          if (!['LiffRegisterValidate'].includes(this.$route.name)) {
            await this.$router.push({
              name: "LiffRegisterValidate",
              params: {
                orgCode: this.$route.params.orgCode,
              },
              query: this.buildPublicRouteQuery(additionalQuery),
            });
            return false;
          }
          break;
        case "email_verify_page":
          if (! ['LiffRegisterEmailValidate'].includes(this.$route.name)) {
            await this.$router.push({
              name: "LiffRegisterEmailValidate",
              params: {
                orgCode: this.$route.params.orgCode,
              },
              query: this.buildPublicRouteQuery(additionalQuery),
            });
            return false;
          }
          break;
        case "verify_form_page":
          if (!['LiffRegisterTerms', 'LiffRegisterValidate', 'LiffRegisterVerifyForm'].includes(this.$route.name)) {
            await this.$router.push({
              name: "LiffRegisterVerifyForm",
              params: {
                orgCode: this.$route.params.orgCode,
              },
              query: this.buildPublicRouteQuery(additionalQuery),
            });
            return false;
          }
          break;
        case "external_register":
          await this.$router.push({
            name: "LiffRegisterNotRegister",
          });
          return false;
        case "check_overwrite_binding":
          await this.$router.push({
            name: "LiffRegisterOverwriteCheck",
          });
          return false;
        case "field_missed":
          await this.$router.push({
            name: "LiffRegisterInfo",
            params: {
              type: "field_missed"
            }
          });
          return false;
        case "provider_error":
          await this.$router.push({
            name: "LiffRegisterProviderError",
          });
          return false;
        case "binding_error":
          this.$router.push({
            name: "LiffRegisterBindingError",
          });
          return false;
        case "bypass_remind_page":
          await this.$router.push({
            name: "LiffRegisterBypassRemind",
          });
          return false;
        case "duplicate_registration":
          await this.$router.push({
            name: "LiffRegisterDuplicateRegistration",
          });
          return false;
        default:
          return true;
      }
    },
    getModuleConfig(key) {
      return this.$store.getters["liffModule/getConfig"](MODULE_CODE, key);
    },
    getMobileVerifyConfig(key, defaultValue = undefined) {
      const value = this.$store.getters["liffModule/getConfig"](MOBILE_VERIFY_CONFIG_MODULE_CODE, key);

      return value === undefined || value === null ? defaultValue : value;
    },
    getMobileVerifyCountryCode() {
      const countryCode = this.getMobileVerifyConfig("country_code", "+886");

      return typeof countryCode === "string" && countryCode.trim()
        ? countryCode
        : "+886";
    },
    getMobileVerifyRetryGapSeconds(defaultValue = 120) {
      const retryGapSeconds = parseInt(
        this.getMobileVerifyConfig("retry_gap_seconds", defaultValue),
        10
      );

      return Number.isFinite(retryGapSeconds) && retryGapSeconds > 0
        ? retryGapSeconds
        : defaultValue;
    },
    getMobileVerifyCodeLength(defaultValue = null) {
      const value = this.getMobileVerifyConfig("verify_code_length", defaultValue);

      if (value === null || value === "null" || value === undefined || value === "") {
        return defaultValue;
      }

      const verifyCodeLength = parseInt(value, 10);

      return Number.isFinite(verifyCodeLength) && verifyCodeLength > 0
        ? verifyCodeLength
        : defaultValue;
    },
    async goSuccess() {
      if (this.$store.getters['route/hasIntended']) {
        await this.$store.dispatch('route/intended')
        return
      }

      var registeredAction = this.getModuleConfig('registered_action')

      switch (registeredAction) {
        case "member-center":
          await this.$router.push({ name: "LiffMemberCenterHome" });
          break;
        case "member-edit":
          await this.$router.push({ name: "LiffMemberCenterEdit" });
          break;
        case "edit_done":
          await this.$router.push({ name: "LiffMemberCenterEditDone" });
          break;
        case "thanks":
          await this.$router.push({ name: "LiffRegisterThanks" });
          break;
        case "intro":
          await this.$router.push({ name: "LiffRegisterIntro" });
          break;
        case "register":
          await this.$router.push({ name: "LiffRegisterIndex" });
          break;
        case "welcome":
        default:
          await this.$router.push({
            name: "LiffRegisterWelcome",
            params: {
              orgCode: this.$route.params.orgCode,
            },
          });
          break;
      }
    },
    trans(key, defaultMessage, params) {
      let trans = this.getModuleConfig(`trans.${key}`) || defaultMessage;

      if (params) {
        trans = trans.replace(/\{(\w+)\}/g, (_, key) => params[key] || '');
      }

      return trans
    },
    buildEmailVerifyQuery(responseData = {}, fallback = {}) {
      const keys = [
        'expired_at',
        'email',
        'from',
      ];

      return keys.reduce((query, key) => {
        const value = responseData[key] ?? fallback[key];

        if (value !== undefined && value !== null && value !== '') {
          query[key] = value;
        }

        return query;
      }, {});
    },
    buildPublicRouteQuery(additionalQuery = {}) {
      const query = this.sanitizeRegisterQuery(additionalQuery);

      delete query.email;
      delete query.expired_at;

      return query;
    },
    setEmailVerifyContext(context = {}) {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
      }

      window.sessionStorage.setItem(
        EMAIL_VERIFY_CONTEXT_KEY,
        JSON.stringify(context)
      );
    },
    getEmailVerifyContext() {
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return {};
      }

      try {
        return JSON.parse(window.sessionStorage.getItem(EMAIL_VERIFY_CONTEXT_KEY)) || {};
      } catch (error) {
        return {};
      }
    },
  }
}
